/**
 * Per-environment configuration for the admin SPA.
 *
 * Everything in this file ships to every browser. Nothing secret goes here,
 * ever; secrets live behind the API (see docs/runbooks/credentials.md).
 *
 * How the environment is selected: the bundle is built once, environment-free.
 * At deploy time the pipeline replaces the __APP_ENV__ placeholder in
 * index.html's app-env meta tag with the environment name, and this module
 * picks the matching entry. The dev server never stamps anything, so dev mode
 * short-circuits on import.meta.env.DEV. A deployed bundle that was never
 * stamped throws on boot: silently defaulting could point one environment's
 * UI at another environment's expectations, and `vite preview` of a local
 * build fails the same way by design (stamp index.html manually if you need
 * it).
 */

export type AppEnvironment = 'development' | 'preview' | 'staging' | 'production';

const PLACEHOLDER = '__APP_ENV__';

function isAppEnvironment(value: string): value is AppEnvironment {
    return (
        value === 'development' ||
        value === 'preview' ||
        value === 'staging' ||
        value === 'production'
    );
}

/** Exported for tests; app code reads `appEnv` instead. */
export function detectEnvironment(doc: Document = document): AppEnvironment {
    if (import.meta.env.DEV) return 'development';

    const stamped = doc.querySelector('meta[name="app-env"]')?.getAttribute('content') ?? '';
    if (isAppEnvironment(stamped)) return stamped;

    throw new Error(
        stamped === PLACEHOLDER || stamped === ''
            ? 'app-env was never stamped: this bundle was deployed without the ' +
                  `deploy pipeline replacing ${PLACEHOLDER} in index.html`
            : `app-env is stamped with unknown environment '${stamped}'`,
    );
}

export const appEnv: AppEnvironment = detectEnvironment();

const RELEASE_PLACEHOLDER = '__APP_RELEASE__';

/**
 * The release the deploy stamped into index.html's app-release meta tag beside the
 * environment: the image tag of the build, `sha-<12>` or a preview's `pr-<n>-<sha12>`, the same
 * name the API reports under, so one release in Sentry holds both halves.
 *
 * Unlike the environment it is never required. A bundle nobody stamped (the dev server, a
 * `vite preview`) has no release, which only leaves Sentry unable to say which deploy an
 * error came from.
 *
 * Exported for tests; app code reads `appRelease` instead.
 */
export function detectRelease(doc: Document = document): string | undefined {
    const stamped = doc.querySelector('meta[name="app-release"]')?.getAttribute('content') ?? '';
    return stamped === '' || stamped === RELEASE_PLACEHOLDER ? undefined : stamped;
}

export const appRelease: string | undefined = detectRelease();

/**
 * How Shake, the in-app bug reporter, behaves in one environment.
 *
 * One Shake app per environment, so a report filed from a pull request preview never lands
 * in production's inbox. The app key is public by design: it names the app a report is sent
 * to, and authorises nothing, which is why it sits here rather than behind the credentials
 * system (docs/runbooks/credentials.md). It is a literal rather than a VITE_ variable on
 * purpose, for the same reason the rest of this file exists: a VITE_ variable is inlined at
 * build time, and this bundle is built once and promoted.
 */
export interface ShakeSettings {
    apiKey: string;
    /**
     * Continuous recording of the screen. Off in production, where nearly every screen shows
     * a real customer's name, address or order. A screenshot is the one screen the reporter
     * chose to send and can mask by hand; a replay is everything they scrolled past on the
     * way there. Preview and staging do not run against the live database (ADR 0007), so
     * there it is free, and it is the most useful thing in a bug report.
     */
    sessionReplay: boolean;
    /**
     * Request and response bodies. Off in production, where `lib/shake.ts` installs a filter
     * that keeps the shape of a call and drops its contents. Credential-bearing paths are
     * stripped in every environment regardless of this flag.
     */
    networkPayloads: boolean;
}

/**
 * How Sentry, the error and performance monitor, behaves in one environment (ADR 0101).
 *
 * One Sentry project for the admin, `admin-web`, with the environment as a tag on every event,
 * so the DSN is the same in each. A DSN is public by design for the same reason Shake's key
 * is: it names the project an event goes to and authorises nothing but sending one.
 */
export interface SentrySettings {
    dsn: string;
    /** The share of page loads and navigations traced end to end, through the API, from 0 to 1. */
    tracesSampleRate: number;
}

/** Public, per-environment values. Add fields here as they become real. */
export interface AppConfig {
    /** Marks non-production environments in the UI so nobody edits the wrong data. */
    showEnvironmentBadge: boolean;
    /** `null` means Shake never starts here; on the dev server you report a bug by talking. */
    shake: ShakeSettings | null;
    /** `null` means Sentry never starts here; the dev server's errors are in its console. */
    sentry: SentrySettings | null;
}

const SENTRY_DSN =
    'https://ba6a5cc5b2a1746e4201b81ece8e27d0@o4512361531113472.ingest.de.sentry.io/4512361574563920';

/*
 * The `nosemgrep` lines below are not a smell being waved through. A Shake app key is a
 * high-entropy string next to the word `apiKey`, which is what a secret looks like to a
 * scanner that cannot know it is a public identifier served to every browser. Both scanners
 * this repo runs flag it, so both are told once, narrowly: semgrep here, per line and per
 * rule, and gitleaks by path in .gitleaks.toml. See ADR 0035.
 */
const configs: Record<AppEnvironment, AppConfig> = {
    development: {
        showEnvironmentBadge: true,
        shake: null,
        sentry: null,
    },
    preview: {
        showEnvironmentBadge: true,
        shake: {
            // nosemgrep: generic.secrets.security.detected-generic-api-key.detected-generic-api-key
            apiKey: 'WqwMFbRYFf7frbY59u9nZXbxPaq3FNiqZ4UdgOAx93s0dAp1s5kglOP',
            sessionReplay: true,
            networkPayloads: true,
        },
        sentry: { dsn: SENTRY_DSN, tracesSampleRate: 1 },
    },
    staging: {
        showEnvironmentBadge: true,
        shake: {
            // nosemgrep: generic.secrets.security.detected-generic-api-key.detected-generic-api-key
            apiKey: 'VzecelA7iRgIV7mibjc7aL1eabZ3GxBPUpaChUYRjx6eFFgyQmXmPW7',
            sessionReplay: true,
            networkPayloads: true,
        },
        sentry: { dsn: SENTRY_DSN, tracesSampleRate: 1 },
    },
    production: {
        showEnvironmentBadge: false,
        shake: {
            // nosemgrep: generic.secrets.security.detected-generic-api-key.detected-generic-api-key
            apiKey: 'APbU317Ux8xs5VZADRsrBUFKxh44v3sXsHo2d7lzB9gTQxc9CjMFh3N',
            sessionReplay: false,
            networkPayloads: false,
        },
        // A fifth of page loads: enough to see what is slow, without a trace of every screen.
        sentry: { dsn: SENTRY_DSN, tracesSampleRate: 0.2 },
    },
};

export const appConfig: AppConfig = configs[appEnv];
